/**
 * EnchantScreen - Minecraft-style enchanting table UI
 *
 * Dark purple/obsidian themed. Shows:
 *  - Item slot (place tool here)
 *  - Lapis slot (needs lapis lazuli)
 *  - 3 enchantment tier buttons with XP cost
 *  - Current XP level display
 *
 * Close with Escape or X button.
 */

import { renderBlockIcon, renderItemIcon } from './Hotbar.js';
import { isTool, getToolName, getToolColor } from '../items/ToolSystem.js';
import { ItemId, getItemName as registryItemName } from '../items/ItemRegistry.js';
import {
  ENCHANTMENTS,
  generateEnchantOptions,
  applyEnchantment,
  getEnchantments,
  getEnchantDisplayText,
  isEnchantable,
  getEnchantCost,
  getLapisCost,
} from '../items/EnchantSystem.js';

const SLOT_SIZE = 48;
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V'];

export class EnchantScreen {
  constructor(inventory, xpSystem, enchantSystem) {
    this.inventory = inventory;
    this.xpSystem = xpSystem;
    this.isOpen = false;

    // Enchant slots
    this.itemSlot = null;   // tool/weapon placed here { ...toolData }
    this.lapisSlot = null;  // { itemId: ItemId.LAPIS, count: N }

    // Currently generated options (3 tiers)
    this.options = [null, null, null];

    // Source slot index for returning items on close
    this._itemSourceIdx = -1;

    this._build();
  }

  // ─── Build DOM ──────────────────────────────────────────────
  _build() {
    // Overlay
    this.overlay = document.createElement('div');
    this.overlay.id = 'enchant-overlay';
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.65); z-index: 500;
      display: none; justify-content: center; align-items: center;
      cursor: default;
    `;

    // Click overlay to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    // Panel
    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #2A1A3A;
      border: 4px solid #0E0618;
      border-top-color: #5A3A7A; border-left-color: #5A3A7A;
      padding: 16px 20px;
      font-family: 'Courier New', monospace;
      color: #D0C0E0;
      user-select: none;
      image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.5), inset 0 0 0 2px #3A2050;
      min-width: 340px;
      position: relative;
    `;

    // Close button
    const closeBtn = document.createElement('div');
    closeBtn.textContent = 'X';
    closeBtn.style.cssText = `
      position: absolute; top: 6px; right: 10px;
      cursor: pointer; font-size: 16px; font-weight: bold;
      color: #AA88CC; line-height: 1;
    `;
    closeBtn.addEventListener('click', () => this.close());
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.color = '#FFFFFF'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.color = '#AA88CC'; });
    this.panel.appendChild(closeBtn);

    // Title
    const title = document.createElement('div');
    title.textContent = 'Mesa de Encantamientos';
    title.style.cssText = `
      font-size: 15px; font-weight: bold; margin-bottom: 12px;
      color: #C8A0F0;
      text-shadow: 1px 1px 0 #000;
      text-align: center;
    `;
    this.panel.appendChild(title);

    // Top area: item slot + lapis slot + enchant options
    const topArea = document.createElement('div');
    topArea.style.cssText = `
      display: flex;
      gap: 16px;
      align-items: flex-start;
      margin-bottom: 14px;
    `;

    // Left: item slot + lapis slot
    const slotsCol = document.createElement('div');
    slotsCol.style.cssText = 'display:flex;flex-direction:column;gap:8px;align-items:center;';

    const itemLabel = document.createElement('div');
    itemLabel.textContent = 'Objeto';
    itemLabel.style.cssText = 'font-size:10px;color:#9A7AB8;';
    slotsCol.appendChild(itemLabel);

    this.itemSlotEl = this._createSlot('#3A2050');
    this.itemSlotEl.addEventListener('click', () => this._onItemSlotClick());
    slotsCol.appendChild(this.itemSlotEl);

    const lapisLabel = document.createElement('div');
    lapisLabel.textContent = 'Lapis';
    lapisLabel.style.cssText = 'font-size:10px;color:#9A7AB8;margin-top:4px;';
    slotsCol.appendChild(lapisLabel);

    this.lapisSlotEl = this._createSlot('#2A1840');
    this.lapisSlotEl.addEventListener('click', () => this._onLapisSlotClick());
    slotsCol.appendChild(this.lapisSlotEl);

    topArea.appendChild(slotsCol);

    // Right: 3 enchant option buttons
    const optionsCol = document.createElement('div');
    optionsCol.style.cssText = 'display:flex;flex-direction:column;gap:6px;flex:1;';

    this.optionBtns = [];
    for (let tier = 1; tier <= 3; tier++) {
      const btn = document.createElement('div');
      btn.style.cssText = `
        background: #1A0E28;
        border: 2px solid #3A2050;
        padding: 8px 12px;
        cursor: pointer;
        transition: background 0.15s, border-color 0.15s;
        min-height: 32px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      `;
      btn.addEventListener('mouseenter', () => {
        if (this._canEnchant(tier - 1)) {
          btn.style.background = '#2A1A40';
          btn.style.borderColor = '#6A4A90';
        }
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = '#1A0E28';
        btn.style.borderColor = '#3A2050';
      });
      btn.addEventListener('click', () => this._onEnchantClick(tier - 1));
      optionsCol.appendChild(btn);
      this.optionBtns.push(btn);
    }

    topArea.appendChild(optionsCol);
    this.panel.appendChild(topArea);

    // XP level display
    this.xpDisplay = document.createElement('div');
    this.xpDisplay.style.cssText = `
      text-align: center;
      font-size: 13px;
      color: #80E820;
      text-shadow: 1px 1px 0 #000;
      margin-bottom: 10px;
      padding: 4px;
      background: rgba(0,0,0,0.3);
      border-radius: 2px;
    `;
    this.panel.appendChild(this.xpDisplay);

    // Enchantment info on current item
    this.enchantInfo = document.createElement('div');
    this.enchantInfo.style.cssText = `
      text-align: center;
      font-size: 11px;
      color: #A088C0;
      margin-bottom: 10px;
      min-height: 14px;
    `;
    this.panel.appendChild(this.enchantInfo);

    // Separator
    const sep = document.createElement('div');
    sep.style.cssText = 'height: 2px; background: #3A2050; margin: 6px 0;';
    this.panel.appendChild(sep);

    // Player inventory label
    const invLabel = document.createElement('div');
    invLabel.textContent = 'Inventario';
    invLabel.style.cssText = 'font-size: 11px; margin-bottom: 4px; color: #8A6AA8;';
    this.panel.appendChild(invLabel);

    // Player storage (27 slots, unified indices 9-35)
    this.playerGrid = document.createElement('div');
    this.playerGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(9, ${SLOT_SIZE - 4}px);
      gap: 2px;
      margin-bottom: 6px;
    `;
    this.playerSlots = [];
    for (let i = 0; i < 27; i++) {
      const slot = this._createSlot('#3A2A50');
      slot.addEventListener('click', () => this._onPlayerSlotClick(i + 9));
      this.playerGrid.appendChild(slot);
      this.playerSlots.push(slot);
    }
    this.panel.appendChild(this.playerGrid);

    // Hotbar separator
    const hotbarSep = document.createElement('div');
    hotbarSep.style.cssText = 'height: 2px; background: #3A2050; margin: 4px 0;';
    this.panel.appendChild(hotbarSep);

    // Hotbar (9 slots, unified indices 0-8)
    this.hotbarGrid = document.createElement('div');
    this.hotbarGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(9, ${SLOT_SIZE - 4}px);
      gap: 2px;
    `;
    this.hotbarSlots = [];
    for (let i = 0; i < 9; i++) {
      const slot = this._createSlot('#3A2A50');
      slot.addEventListener('click', () => this._onPlayerSlotClick(i));
      this.hotbarGrid.appendChild(slot);
      this.hotbarSlots.push(slot);
    }
    this.panel.appendChild(this.hotbarGrid);

    this.overlay.appendChild(this.panel);
    document.body.appendChild(this.overlay);

    // ESC key handler
    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    };
    // keydown listener added in open(), removed in close()
  }

  // ─── Slot factory ────────────────────────────────────────────
  _createSlot(bgColor) {
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE - 4}px; height: ${SLOT_SIZE - 4}px;
      background: ${bgColor || '#3A2A50'};
      border: 2px solid #1A0A28;
      border-top-color: #5A3A7A; border-left-color: #5A3A7A;
      position: relative; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
    `;
    return slot;
  }

  // ─── Render a slot with an item ─────────────────────────────
  _renderSlot(slotEl, item) {
    slotEl.innerHTML = '';
    if (!item) return;

    if (isTool(item)) {
      // Tool icon: colored square with tool type initial
      const color = getToolColor(item);
      const name = getToolName(item);
      const initial = item.toolType[0].toUpperCase();
      slotEl.innerHTML = `
        <div style="width:28px;height:28px;background:${color};border:2px solid rgba(0,0,0,0.3);
          border-radius:2px;display:flex;align-items:center;justify-content:center;
          font-size:14px;font-weight:bold;color:white;text-shadow:1px 1px 0 #000;">
          ${initial}
        </div>
      `;
      // Enchantment glow
      if (item.enchantments && item.enchantments.length > 0) {
        const glow = document.createElement('div');
        glow.style.cssText = `
          position:absolute;inset:0;
          background:linear-gradient(135deg, rgba(128,100,255,0.2), rgba(200,100,255,0.1));
          pointer-events:none;
          border:1px solid rgba(160,100,255,0.3);
        `;
        slotEl.appendChild(glow);
      }
    } else if (item.itemId !== undefined) {
      slotEl.innerHTML = renderItemIcon(item.itemId, 28);
      if (item.count > 1) {
        const c = document.createElement('div');
        c.textContent = item.count;
        c.style.cssText = `
          position:absolute;bottom:1px;right:3px;
          font-size:10px;font-weight:bold;color:white;
          text-shadow:1px 1px 0 #000;pointer-events:none;
        `;
        slotEl.appendChild(c);
      }
    } else if (item.blockId !== undefined) {
      slotEl.innerHTML = renderBlockIcon(item.blockId, 28);
      if (item.count > 1) {
        const c = document.createElement('div');
        c.textContent = item.count;
        c.style.cssText = `
          position:absolute;bottom:1px;right:3px;
          font-size:10px;font-weight:bold;color:white;
          text-shadow:1px 1px 0 #000;pointer-events:none;
        `;
        slotEl.appendChild(c);
      }
    }
  }

  // ─── Item slot click: take/place tool ───────────────────────
  _onItemSlotClick() {
    // If there's already an item in the enchant slot, return it
    if (this.itemSlot) {
      this._returnItemToInventory(this.itemSlot);
      this.itemSlot = null;
      this._itemSourceIdx = -1;
      this.options = [null, null, null];
      this._render();
    }
  }

  // ─── Lapis slot click: take/place lapis ─────────────────────
  _onLapisSlotClick() {
    if (this.lapisSlot) {
      // Return lapis to inventory
      this.inventory.addItem(ItemId.LAPIS, this.lapisSlot.count);
      this.lapisSlot = null;
      this._render();
    }
  }

  // ─── Player inventory slot click ────────────────────────────
  _onPlayerSlotClick(unifiedIdx) {
    const item = this.inventory.getUnifiedSlot(unifiedIdx);
    if (!item) return;

    // If it's a tool and no tool in enchant slot -> place it
    if (isTool(item) && !this.itemSlot) {
      this.itemSlot = item;
      this._itemSourceIdx = unifiedIdx;
      this.inventory.setUnifiedSlot(unifiedIdx, null);
      // Generate enchant options
      this.options = generateEnchantOptions(this.itemSlot);
      this._render();
      return;
    }

    // If it's lapis and no lapis in slot -> place it
    if (item.itemId === ItemId.LAPIS && !this.lapisSlot) {
      this.lapisSlot = { itemId: ItemId.LAPIS, count: item.count };
      this.inventory.setUnifiedSlot(unifiedIdx, null);
      this._render();
      return;
    }

    // If it's lapis and already have some -> add to existing
    if (item.itemId === ItemId.LAPIS && this.lapisSlot) {
      this.lapisSlot.count += item.count;
      this.inventory.setUnifiedSlot(unifiedIdx, null);
      this._render();
      return;
    }
  }

  // ─── Enchant button click ──────────────────────────────────
  _onEnchantClick(optionIdx) {
    if (!this._canEnchant(optionIdx)) return;

    const option = this.options[optionIdx];
    const tier = option.tier;
    const cost = option.cost;
    const lapisCost = option.lapisCost;

    // Spend XP levels
    if (!this.xpSystem.spendLevels(cost)) return;

    // Consume lapis
    this.lapisSlot.count -= lapisCost;
    if (this.lapisSlot.count <= 0) {
      this.lapisSlot = null;
    }

    // Apply enchantment
    applyEnchantment(this.itemSlot, option.enchId, option.enchLevel);

    // Regenerate options
    this.options = generateEnchantOptions(this.itemSlot);
    this._render();
  }

  // ─── Can enchant check ─────────────────────────────────────
  _canEnchant(optionIdx) {
    const option = this.options[optionIdx];
    if (!option) return false;
    if (!this.itemSlot) return false;
    if (!this.lapisSlot || this.lapisSlot.count < option.lapisCost) return false;
    if (this.xpSystem.level < option.cost) return false;
    return true;
  }

  // ─── Return item to inventory ──────────────────────────────
  _returnItemToInventory(item) {
    if (!item) return;
    if (isTool(item)) {
      // Try to put it back in original slot first
      if (this._itemSourceIdx >= 0 && !this.inventory.getUnifiedSlot(this._itemSourceIdx)) {
        this.inventory.setUnifiedSlot(this._itemSourceIdx, item);
      } else {
        this.inventory.addTool(item);
      }
    } else if (item.itemId !== undefined) {
      this.inventory.addItem(item.itemId, item.count);
    } else if (item.blockId !== undefined) {
      for (let c = 0; c < (item.count || 1); c++) {
        this.inventory.addBlock(item.blockId);
      }
    }
  }

  // ─── Open / Close / Toggle ──────────────────────────────────
  open() {
    this.isOpen = true;
    this.overlay.style.display = 'flex';
    this.itemSlot = null;
    this.lapisSlot = null;
    this._itemSourceIdx = -1;
    this.options = [null, null, null];
    document.addEventListener('keydown', this._escHandler);
    this._render();
  }

  close() {
    if (!this.isOpen) return;

    // Return items to inventory
    if (this.itemSlot) {
      this._returnItemToInventory(this.itemSlot);
      this.itemSlot = null;
    }
    if (this.lapisSlot) {
      this.inventory.addItem(ItemId.LAPIS, this.lapisSlot.count);
      this.lapisSlot = null;
    }

    this._itemSourceIdx = -1;
    this.options = [null, null, null];
    this.isOpen = false;
    this.overlay.style.display = 'none';
    document.removeEventListener('keydown', this._escHandler);
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  // ─── Full render ───────────────────────────────────────────
  _render() {
    // Item slot
    this._renderSlot(this.itemSlotEl, this.itemSlot);

    // Lapis slot
    this._renderSlot(this.lapisSlotEl, this.lapisSlot);

    // XP display
    const level = this.xpSystem ? this.xpSystem.level : 0;
    this.xpDisplay.textContent = `Nivel de XP: ${level}`;

    // Enchant info on current item
    if (this.itemSlot) {
      const text = getEnchantDisplayText(this.itemSlot);
      this.enchantInfo.textContent = text || 'Sin encantamientos';
      this.enchantInfo.style.color = text ? '#B090E0' : '#6A5080';
    } else {
      this.enchantInfo.textContent = 'Coloca una herramienta para encantar';
      this.enchantInfo.style.color = '#6A5080';
    }

    // Option buttons
    for (let i = 0; i < 3; i++) {
      const btn = this.optionBtns[i];
      const option = this.options[i];
      const tier = i + 1;

      if (!option) {
        btn.innerHTML = `
          <span style="color:#4A3060;font-size:12px;">Tier ${tier}</span>
          <span style="color:#3A2040;font-size:11px;">---</span>
        `;
        btn.style.cursor = 'default';
        btn.style.opacity = '0.5';
        continue;
      }

      const canDo = this._canEnchant(i);
      const ench = ENCHANTMENTS[option.enchId];
      const enchName = ench ? ench.name : option.enchId;
      const enchLevel = ROMAN[option.enchLevel] || String(option.enchLevel);
      const costColor = canDo ? '#80E820' : '#AA3333';
      const nameColor = canDo ? '#C8A0F0' : '#6A5080';
      const lapisColor = canDo ? '#4488CC' : '#AA3333';

      btn.innerHTML = `
        <div style="display:flex;flex-direction:column;gap:2px;">
          <span style="color:${nameColor};font-size:12px;font-weight:bold;">
            ${enchName} ${enchLevel}
          </span>
          <span style="color:#7A6090;font-size:10px;">${ench ? ench.description : ''}</span>
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:1px;">
          <span style="color:${costColor};font-size:11px;font-weight:bold;">${option.cost} Niv.</span>
          <span style="color:${lapisColor};font-size:10px;">${option.lapisCost} Lapis</span>
        </div>
      `;
      btn.style.cursor = canDo ? 'pointer' : 'not-allowed';
      btn.style.opacity = canDo ? '1' : '0.6';
    }

    // Player inventory slots
    for (let i = 0; i < 27; i++) {
      const item = this.inventory.getUnifiedSlot(i + 9);
      this._renderSlot(this.playerSlots[i], item);
    }

    // Hotbar slots
    for (let i = 0; i < 9; i++) {
      const item = this.inventory.getUnifiedSlot(i);
      this._renderSlot(this.hotbarSlots[i], item);
    }
  }
}
